import { ArrowIcon } from "@/assets/images/svgIcons/arrowIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { ResumeCardPriceDisplay } from "@/modules/finances/components/resumeCardPriceDisplay"
import { useFinanceStats } from "@/modules/finances/hooks/useFinance"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { cn } from "@/modules/lib/utils"
import { useBoxNavigate } from "@/modules/navigation/hooks/useBoxNavigate"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { CircleQuestionMarkIcon } from "lucide-react"

type TransactionsMyWalletCardProps = {
  className?: string
  hideViewPayoutsButton?: boolean
} & React.ComponentProps<"div">

export function TransactionsMyWalletCard({
  className,
  hideViewPayoutsButton = false,
  ...props
}: TransactionsMyWalletCardProps) {
  const boxNavigate = useBoxNavigate()
  const mySpaceNavigate = useMySpaceNavigate()
  const { result: box } = useCurrentBox()
  const [period] = usePeriodParam()
  const canSeeFinancial = useBoxStaffPermission(BoxStaffPermissions.SEE_FINANCIAL)
  const { data: stats, isLoading } = useFinanceStats(canSeeFinancial || !box, period)

  if (isLoading || !stats) return <LoadingSpinner />

  const handleViewPayoutsClick = () => {
    if (box?.id) {
      boxNavigate(`${Path.Box.Finances.Root}/${Path.Box.Finances.Payouts}`)
    } else {
      mySpaceNavigate(`${Path.MySpace.Finances.Root}/${Path.MySpace.Finances.Payouts}`)
    }
  }

  const nextPayoutAmount = stats.stats.currentBalance.available
  const nextPayoutVerifyingAmount = stats.stats.currentBalance.pending

  return (
    <div
      className={cn(
        "flex max-w-120 flex-col justify-between overflow-hidden rounded-4xl bg-surface-background-secondary px-3 py-2 md:gap-6 md:px-8 md:py-6",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col gap-2">
        <Text fontStyle="TL">{word("finances.page.transactions.myWallet.title")}</Text>
      </div>

      <div className="flex flex-1 flex-col items-start justify-between gap-3 overflow-hidden 2xl:flex-row">
        <div className="flex flex-1/2 flex-col gap-1">
          <div className="flex flex-row items-center gap-1">
            <ResumeCardPriceDisplay
              price={{ value: nextPayoutAmount, currency: stats.stats.currency }}
              wholePartFontStyle="HM"
              decimalPartFontStyle="TL"
              wholePartClassName={nextPayoutAmount > 0 ? "text-textOnSurface-positive" : "text-textOnSurface-subtle"}
              decimalPartClassName={nextPayoutAmount > 0 ? "text-textOnSurface-positive" : "text-textOnSurface-subtle"}
            />
            <Tooltip>
              <TooltipTrigger
                render={<CircleQuestionMarkIcon color="currentColor" className="size-4 text-textOnSurface-subtle" />}
              />
              <TooltipContent side="bottom" className="max-w-120 wrap-break-word whitespace-break-spaces">
                <Text fontStyle="BM">{word("finances.page.transactions.myWallet.nextPayoutTooltip")}</Text>
              </TooltipContent>
            </Tooltip>
          </div>

          <Text fontStyle="LM" className="text-textOnSurface-muted">
            {word("finances.page.transactions.myWallet.nextPayout")}
          </Text>
        </div>

        <div className="flex flex-1/2 flex-col items-end gap-1">
          <div className="flex flex-row items-center gap-1">
            <ResumeCardPriceDisplay
              price={{ value: nextPayoutVerifyingAmount, currency: stats.stats.currency }}
              wholePartFontStyle="TL"
              decimalPartFontStyle="TS"
              wholePartClassName="text-textOnSurface-subtle"
              decimalPartClassName="text-textOnSurface-subtle"
            />
            <Tooltip>
              <TooltipTrigger
                render={<CircleQuestionMarkIcon color="currentColor" className="size-4 text-textOnSurface-subtle" />}
              />
              <TooltipContent side="bottom" className="max-w-120 wrap-break-word whitespace-break-spaces">
                <Text fontStyle="BM">{word("finances.page.transactions.resume.fundsInVerificationTooltip")}</Text>
              </TooltipContent>
            </Tooltip>
          </div>

          <Text fontStyle="LM" className="text-textOnSurface-muted">
            {word("finances.page.transactions.myWallet.fundsInVerification")}
          </Text>
        </div>
      </div>

      {!hideViewPayoutsButton && (
        <Button variant="underline" size="lg" onClick={() => void handleViewPayoutsClick()} className="self-end">
          {word("finances.page.transactions.myWallet.viewPayouts")}
          <ArrowIcon arrowDirection="right" color="currentColor" className="size-4" />
        </Button>
      )}
    </div>
  )
}
